import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const RecruitmentProcessDurationCalculator = () => {
  const [inputData, setInputData] = useState({
    jobLevel: 'mid-level',
    industry: 'technology',
    companySize: 'medium',
    useCustomStages: false,
    sourcing: '',
    screening: '',
    interviewing: '',
    offerNegotiation: '',
    onboardingPreparation: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  // Sample recruitment duration data (in days)
  const recruitmentData = {
    'entry-level': {
      technology: {
        small: { sourcing: 5, screening: 3, interviewing: 7, offerNegotiation: 2, onboardingPreparation: 3 },
        medium: { sourcing: 7, screening: 4, interviewing: 10, offerNegotiation: 3, onboardingPreparation: 4 },
        large: { sourcing: 10, screening: 5, interviewing: 14, offerNegotiation: 4, onboardingPreparation: 5 },
        enterprise: { sourcing: 14, screening: 7, interviewing: 21, offerNegotiation: 5, onboardingPreparation: 7 }
      },
      healthcare: {
        small: { sourcing: 7, screening: 4, interviewing: 10, offerNegotiation: 3, onboardingPreparation: 5 },
        medium: { sourcing: 10, screening: 5, interviewing: 14, offerNegotiation: 4, onboardingPreparation: 7 },
        large: { sourcing: 14, screening: 7, interviewing: 21, offerNegotiation: 5, onboardingPreparation: 10 },
        enterprise: { sourcing: 21, screening: 10, interviewing: 28, offerNegotiation: 7, onboardingPreparation: 14 }
      },
      finance: {
        small: { sourcing: 5, screening: 3, interviewing: 7, offerNegotiation: 2, onboardingPreparation: 3 },
        medium: { sourcing: 7, screening: 4, interviewing: 10, offerNegotiation: 3, onboardingPreparation: 4 },
        large: { sourcing: 10, screening: 5, interviewing: 14, offerNegotiation: 4, onboardingPreparation: 5 },
        enterprise: { sourcing: 14, screening: 7, interviewing: 21, offerNegotiation: 5, onboardingPreparation: 7 }
      }
    },
    'mid-level': {
      technology: {
        small: { sourcing: 10, screening: 5, interviewing: 14, offerNegotiation: 4, onboardingPreparation: 5 },
        medium: { sourcing: 14, screening: 7, interviewing: 21, offerNegotiation: 5, onboardingPreparation: 7 },
        large: { sourcing: 21, screening: 10, interviewing: 28, offerNegotiation: 7, onboardingPreparation: 10 },
        enterprise: { sourcing: 28, screening: 14, interviewing: 35, offerNegotiation: 10, onboardingPreparation: 14 }
      },
      healthcare: {
        small: { sourcing: 14, screening: 7, interviewing: 21, offerNegotiation: 5, onboardingPreparation: 10 },
        medium: { sourcing: 21, screening: 10, interviewing: 28, offerNegotiation: 7, onboardingPreparation: 14 },
        large: { sourcing: 28, screening: 14, interviewing: 35, offerNegotiation: 10, onboardingPreparation: 21 },
        enterprise: { sourcing: 35, screening: 21, interviewing: 42, offerNegotiation: 14, onboardingPreparation: 28 }
      },
      finance: {
        small: { sourcing: 10, screening: 5, interviewing: 14, offerNegotiation: 4, onboardingPreparation: 5 },
        medium: { sourcing: 14, screening: 7, interviewing: 21, offerNegotiation: 5, onboardingPreparation: 7 },
        large: { sourcing: 21, screening: 10, interviewing: 28, offerNegotiation: 7, onboardingPreparation: 10 },
        enterprise: { sourcing: 28, screening: 14, interviewing: 35, offerNegotiation: 10, onboardingPreparation: 14 }
      }
    },
    'senior-level': {
      technology: {
        small: { sourcing: 21, screening: 10, interviewing: 28, offerNegotiation: 7, onboardingPreparation: 10 },
        medium: { sourcing: 28, screening: 14, interviewing: 35, offerNegotiation: 10, onboardingPreparation: 14 },
        large: { sourcing: 35, screening: 21, interviewing: 42, offerNegotiation: 14, onboardingPreparation: 21 },
        enterprise: { sourcing: 42, screening: 28, interviewing: 49, offerNegotiation: 21, onboardingPreparation: 28 }
      },
      healthcare: {
        small: { sourcing: 28, screening: 14, interviewing: 35, offerNegotiation: 10, onboardingPreparation: 21 },
        medium: { sourcing: 35, screening: 21, interviewing: 42, offerNegotiation: 14, onboardingPreparation: 28 },
        large: { sourcing: 42, screening: 28, interviewing: 49, offerNegotiation: 21, onboardingPreparation: 35 },
        enterprise: { sourcing: 49, screening: 35, interviewing: 56, offerNegotiation: 28, onboardingPreparation: 42 }
      },
      finance: {
        small: { sourcing: 21, screening: 10, interviewing: 28, offerNegotiation: 7, onboardingPreparation: 10 },
        medium: { sourcing: 28, screening: 14, interviewing: 35, offerNegotiation: 10, onboardingPreparation: 14 },
        large: { sourcing: 35, screening: 21, interviewing: 42, offerNegotiation: 14, onboardingPreparation: 21 },
        enterprise: { sourcing: 42, screening: 28, interviewing: 49, offerNegotiation: 21, onboardingPreparation: 28 }
      }
    },
    'executive': {
      technology: {
        small: { sourcing: 35, screening: 21, interviewing: 42, offerNegotiation: 14, onboardingPreparation: 21 },
        medium: { sourcing: 42, screening: 28, interviewing: 49, offerNegotiation: 21, onboardingPreparation: 28 },
        large: { sourcing: 49, screening: 35, interviewing: 56, offerNegotiation: 28, onboardingPreparation: 35 },
        enterprise: { sourcing: 56, screening: 42, interviewing: 63, offerNegotiation: 35, onboardingPreparation: 42 }
      },
      healthcare: {
        small: { sourcing: 42, screening: 28, interviewing: 49, offerNegotiation: 21, onboardingPreparation: 28 },
        medium: { sourcing: 49, screening: 35, interviewing: 56, offerNegotiation: 28, onboardingPreparation: 35 },
        large: { sourcing: 56, screening: 42, interviewing: 63, offerNegotiation: 35, onboardingPreparation: 42 },
        enterprise: { sourcing: 63, screening: 49, interviewing: 70, offerNegotiation: 42, onboardingPreparation: 49 }
      },
      finance: {
        small: { sourcing: 35, screening: 21, interviewing: 42, offerNegotiation: 14, onboardingPreparation: 21 },
        medium: { sourcing: 42, screening: 28, interviewing: 49, offerNegotiation: 21, onboardingPreparation: 28 },
        large: { sourcing: 49, screening: 35, interviewing: 56, offerNegotiation: 28, onboardingPreparation: 35 },
        enterprise: { sourcing: 56, screening: 42, interviewing: 63, offerNegotiation: 35, onboardingPreparation: 42 }
      }
    }
  };

  const calculateRecruitmentDuration = () => {
    const { jobLevel, industry, companySize, useCustomStages, sourcing, screening, interviewing, offerNegotiation, onboardingPreparation } = inputData;
    
    // Validate input
    if (!jobLevel || !industry || !companySize) {
      setError('Please select all required parameters');
      return;
    }

    if (useCustomStages) {
      if (!sourcing || !screening || !interviewing || !offerNegotiation || !onboardingPreparation) {
        setError('Please enter all custom stage durations');
        return;
      }

      if (isNaN(sourcing) || isNaN(screening) || isNaN(interviewing) || isNaN(offerNegotiation) || isNaN(onboardingPreparation)) {
        setError('Please enter valid numbers for custom stage durations');
        return;
      }
    }

    let stages;
    if (useCustomStages) {
      stages = {
        sourcing: parseFloat(sourcing),
        screening: parseFloat(screening),
        interviewing: parseFloat(interviewing),
        offerNegotiation: parseFloat(offerNegotiation),
        onboardingPreparation: parseFloat(onboardingPreparation)
      };
    } else {
      stages = recruitmentData[jobLevel][industry][companySize];
    }

    const totalDuration = Object.values(stages).reduce((sum, duration) => sum + duration, 0);

    // Create visualization data
    const stageLabels = ['Sourcing', 'Screening', 'Interviewing', 'Offer Negotiation', 'Onboarding Preparation'];
    const stageDurations = Object.values(stages);

    const chartData = {
      labels: stageLabels,
      datasets: [{
        label: 'Duration (days)',
        data: stageDurations,
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)'
        ],
        borderWidth: 1
      }]
    };

    const calculationSteps = [
      'Step 1: Select recruitment parameters',
      `Job Level: ${jobLevel.charAt(0).toUpperCase() + jobLevel.slice(1)}`,
      `Industry: ${industry.charAt(0).toUpperCase() + industry.slice(1)}`,
      `Company Size: ${companySize.charAt(0).toUpperCase() + companySize.slice(1)}`,
      useCustomStages ? 'Custom stages enabled' : 'Using industry averages',
      '\nStep 2: Calculate each stage duration',
      `Sourcing: ${stages.sourcing} days`,
      `Screening: ${stages.screening} days`,
      `Interviewing: ${stages.interviewing} days`,
      `Offer Negotiation: ${stages.offerNegotiation} days`,
      `Onboarding Preparation: ${stages.onboardingPreparation} days`,
      '\nStep 3: Sum all stages',
      `Total Duration: ${totalDuration} days`
    ];

    setResults({
      jobLevel: jobLevel.charAt(0).toUpperCase() + jobLevel.slice(1),
      industry: industry.charAt(0).toUpperCase() + industry.slice(1),
      companySize: companySize.charAt(0).toUpperCase() + companySize.slice(1),
      useCustomStages,
      stages,
      totalDuration,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    jobLevel: 'mid-level',
    industry: 'technology',
    companySize: 'medium',
    useCustomStages: false,
    totalDuration: 54
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Recruitment Process Duration Calculator | Hiring Timeline Estimator</title>
          <meta name="description" content="Estimate the duration of your recruitment process based on job level, industry, and company size. This calculator helps HR professionals and hiring managers plan and optimize hiring timelines." />
          <meta name="keywords" content="recruitment calculator, hiring duration calculator, recruitment process estimator, HR tools" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">💼</div>
            <h1 className="text-3xl font-bold text-gray-800">Recruitment Process Duration Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Estimate the duration of your recruitment process based on job level, industry, and company size. This calculator helps HR professionals and hiring managers plan and optimize hiring timelines.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Job Level:</label>
              <select
                name="jobLevel"
                value={inputData.jobLevel}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="entry-level">Entry-level</option>
                <option value="mid-level">Mid-level</option>
                <option value="senior-level">Senior-level</option>
                <option value="executive">Executive</option>
              </select>
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Industry:</label>
              <select
                name="industry"
                value={inputData.industry}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="technology">Technology</option>
                <option value="healthcare">Healthcare</option>
                <option value="finance">Finance</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="retail">Retail</option>
                <option value="education">Education</option>
              </select>
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Size:</label>
              <select
                name="companySize"
                value={inputData.companySize}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              >
                <option value="small">Small (1-50 employees)</option>
                <option value="medium">Medium (51-200 employees)</option>
                <option value="large">Large (201-1000 employees)</option>
                <option value="enterprise">Enterprise (1000+ employees)</option>
              </select>
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 flex items-center">
                <input
                  type="checkbox"
                  name="useCustomStages"
                  checked={inputData.useCustomStages}
                  onChange={handleInputChange}
                  className="form-checkbox h-5 w-5 text-pink-600 mr-2"
                />
                Use custom stage durations
              </label>
            </div>

            {inputData.useCustomStages && (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sourcing (days):</label>
                  <input
                    type="text"
                    name="sourcing"
                    value={inputData.sourcing}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Enter days for sourcing"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Screening (days):</label>
                  <input
                    type="text"
                    name="screening"
                    value={inputData.screening}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Enter days for screening"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Interviewing (days):</label>
                  <input
                    type="text"
                    name="interviewing"
                    value={inputData.interviewing}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Enter days for interviewing"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Offer Negotiation (days):</label>
                  <input
                    type="text"
                    name="offerNegotiation"
                    value={inputData.offerNegotiation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Enter days for offer negotiation"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Onboarding Preparation (days):</label>
                  <input
                    type="text"
                    name="onboardingPreparation"
                    value={inputData.onboardingPreparation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Enter days for onboarding preparation"
                  />
                </div>
              </>
            )}
          </div>

          <button
            onClick={calculateRecruitmentDuration}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Recruitment Duration
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Recruitment Parameters</h3>
                  <p className="text-lg">Job Level: {results.jobLevel}</p>
                  <p className="text-lg">Industry: {results.industry}</p>
                  <p className="text-lg">Company Size: {results.companySize}</p>
                  <p className="text-lg">Custom Stages: {results.useCustomStages ? 'Yes' : 'No'}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Estimated Duration</h3>
                  <p className="text-lg font-bold mt-4">Total Recruitment Duration: {results.totalDuration} days</p>
                  <div className="mt-4 space-y-2">
                    {Object.entries(results.stages).map(([stage, duration]) => (
                      <p key={stage} className="text-lg">
                        {stage.charAt(0).toUpperCase() + stage.slice(1)}: {duration} days
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Duration (days)'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Recruitment Stages'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Recruitment Process Timeline'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for a mid-level technology position in a medium-sized company:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Job Level: {exampleData.jobLevel.charAt(0).toUpperCase() + exampleData.jobLevel.slice(1)}</li>
                <li>Industry: {exampleData.industry.charAt(0).toUpperCase() + exampleData.industry.slice(1)}</li>
                <li>Company Size: {exampleData.companySize.charAt(0).toUpperCase() + exampleData.companySize.slice(1)}</li>
                <li>Use Custom Stages: {exampleData.useCustomStages ? 'Yes' : 'No'}</li>
                <li>Total Duration: {exampleData.totalDuration} days</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Select the job level, industry, and company size</li>
                <li>Use pre-defined industry averages for stage durations</li>
                <li>Sum the durations of all recruitment stages</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Recruitment Process Duration Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Select Job Level:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose from entry-level, mid-level, senior-level, or executive</li>
                </ul>
              </li>
              <li><strong>Select Industry:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Select the industry relevant to your organization</li>
                </ul>
              </li>
              <li><strong>Select Company Size:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose the size category that best fits your organization</li>
                </ul>
              </li>
              <li><strong>Custom Stages (Optional):</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Check the box to enable custom stage durations</li>
                  <li>Enter your organization's specific durations for each recruitment stage</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>Total recruitment duration</li>
                  <li>Breakdown by each recruitment stage</li>
                  <li>Visualization of the recruitment timeline</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the total estimated recruitment duration</li>
                  <li>Examine the breakdown by stage to identify potential bottlenecks</li>
                  <li>Use the visualization to better understand the recruitment timeline</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Compare your organization's recruitment duration with industry averages</li>
                <li>Identify stages that might benefit from process improvements</li>
                <li>Use these estimates to set realistic hiring timelines</li>
                <li>Regularly update custom stage durations to reflect process changes</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Recruitment Process Duration <span className="text-blue-600">💼</span></h2>
          <div className="text-gray-600">
            <p>
              Recruitment process duration varies significantly based on several factors:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Job Level:</strong> Higher-level positions typically require longer recruitment processes</li>
              <li><strong>Industry:</strong> Different industries have varying standards and requirements</li>
              <li><strong>Company Size:</strong> Larger organizations often have more complex recruitment processes</li>
              <li><strong>Custom Processes:</strong> Organizations may have unique stages or requirements</li>
              <li><strong>Market Conditions:</strong> Talent availability and competition affect recruitment timelines</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Document Your Process:</strong> Keep detailed records of your recruitment timelines</li>
              <li><strong>Regularly Review:</strong> Periodically assess your recruitment process for efficiency</li>
              <li><strong>Set Realistic Expectations:</strong> Communicate timelines to stakeholders and candidates</li>
              <li><strong>Identify Bottlenecks:</strong> Use duration analysis to find process improvements</li>
              <li><strong> Benchmark:</strong> Compare your process with industry standards</li>
            </ul>
            <p className="italic mt-4">
              Note: These calculations provide general guidelines. Actual recruitment durations may vary based on specific organizational requirements, market conditions, and other factors.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default RecruitmentProcessDurationCalculator;